import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import DocsLayout from "../components/DocsLayout";
import MiddlewareQrModal from "../components/MiddlewareQrModal";
import ServiceIcon from "../components/ServiceIcon";
import { documentationLinks } from "../config/documentationLinks";
import { services } from "../config/services";
import { useI18n } from "../i18n/I18nContext";
import "../App.css";

const dashboardServices = services.filter((service) => !service.hideFromDashboard);
const openApiServices = dashboardServices.filter((service) => service.route);

const Home = () => {
    const { t } = useI18n();
    const [selectedMiddlewareService, setSelectedMiddlewareService] = useState(null);
    const closeMiddlewareModal = useCallback(() => setSelectedMiddlewareService(null), []);

    return (
        <DocsLayout
            eyebrow={t("home.portalCognito")}
            subtitle={t("home.docsSubtitle")}
            title={t("home.docsTitle")}
        >
            {selectedMiddlewareService ? (
                <MiddlewareQrModal service={selectedMiddlewareService} onClose={closeMiddlewareModal} />
            ) : null}

            <section className="overview-grid" aria-label="Resumen del portal">
                <article className="metric-card">
                    <span className="metric-icon">
                        <ServiceIcon name="contracts" />
                    </span>
                    <span>{t("home.contracts")}</span>
                    <strong>{openApiServices.length}</strong>
                    <small>{t("home.openApiPublished")}</small>
                </article>
                <article className="metric-card">
                    <span className="metric-icon">
                        <ServiceIcon name="services" />
                    </span>
                    <span>{t("home.services")}</span>
                    <strong>{dashboardServices.length}</strong>
                    <small>{t("home.availableInSandbox")}</small>
                </article>
                <article className="metric-card">
                    <span className="metric-icon">
                        <ServiceIcon name="auth" />
                    </span>
                    <span>{t("auth.auth")}</span>
                    <strong>{t("auth.cognito")}</strong>
                    <small>{t("auth.tokenActive")}</small>
                </article>
                <article className="metric-card">
                    <span className="metric-icon">
                        <ServiceIcon name="deliverables" />
                    </span>
                    <span>{t("home.deliverables")}</span>
                    <strong>{openApiServices.length + documentationLinks.length}</strong>
                    <small>{t("home.contractsAndDocs")}</small>
                </article>
            </section>

            <section className="service-grid">
                {dashboardServices.map((service) => (
                    <article className="service-card" key={service.route || service.externalUrl}>
                        <div className="service-card-top">
                            <span className="service-icon" aria-hidden="true">
                                <ServiceIcon name={service.icon} />
                            </span>
                            <div>
                                <h2>{t(`services.${service.key}.name`)}</h2>
                                <p>{t(`services.${service.key}.description`)}</p>
                            </div>
                        </div>
                        <div className="service-meta">
                            <code>{service.domain}</code>
                            <span>{service.version}</span>
                        </div>
                        <div className="service-endpoint">
                            <span>{service.method}</span>
                            <code>{service.endpoint}</code>
                        </div>
                        {service.externalUrl ? (
                            <button
                                className="service-link"
                                type="button"
                                onClick={() => setSelectedMiddlewareService(service)}
                            >
                                {t(`services.${service.key}.actionLabel`)}
                            </button>
                        ) : (
                            <Link className="service-link" to={service.route}>
                                {t("home.viewSwagger")}
                            </Link>
                        )}
                    </article>
                ))}
            </section>

            <section className="documentation-panel">
                <div className="contracts-panel-header">
                    <div>
                        <p className="docs-eyebrow">{t("home.documentation")}</p>
                        <h2>{t("home.sdkDocumentation")}</h2>
                    </div>
                    <span>{documentationLinks.length} {t("home.resource")}</span>
                </div>
                <div className="documentation-grid">
                    {documentationLinks.map((item) => (
                        <article className="documentation-card" key={item.key}>
                            <span className="service-icon" aria-hidden="true">
                                <ServiceIcon name={item.icon} />
                            </span>
                            <div>
                                <h3>{t(`documentation.${item.key}.name`)}</h3>
                                <p>{t(`documentation.${item.key}.description`)}</p>
                                <code>{item.domain}</code>
                            </div>
                            <a className="service-link" href={item.externalUrl} rel="noreferrer" target="_blank">
                                {t("home.openDocumentation")}
                            </a>
                        </article>
                    ))}
                </div>
            </section>

            <section className="contracts-panel">
                <div className="contracts-panel-header">
                    <div>
                        <p className="docs-eyebrow">OpenAPI</p>
                        <h2>{t("home.availableContracts")}</h2>
                    </div>
                    <span>{openApiServices.length} {t("home.services").toLowerCase()}</span>
                </div>
                <div className="contracts-table" role="table" aria-label={t("home.availableContracts")}>
                    <div className="contracts-row contracts-row-head" role="row">
                        <span>{t("home.services")}</span>
                        <span>{t("contract.method")}</span>
                        <span>Endpoint</span>
                        <span>{t("contract.version")}</span>
                    </div>
                    {openApiServices.map((service) => (
                        <div className="contracts-row" role="row" key={service.route}>
                            <span>{t(`services.${service.key}.shortName`)}</span>
                            <strong className={`method-badge method-${service.method.toLowerCase().split("/")[0]}`}>
                                {service.method}
                            </strong>
                            <code>{service.endpoint}</code>
                            <span>{service.version}</span>
                        </div>
                    ))}
                </div>
            </section>
        </DocsLayout>
    );
};

export default Home;
